import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { DashboardPage } from '../modules/dashboard/DashboardPage';
import { PageState } from '../components/ui';
import { ProtectedRoute, PublicOnlyRoute } from '../auth/ProtectedRoute';
import { LoginPage } from '../auth/LoginPage';
import { RegisterPage } from '../auth/RegisterPage';
import { ForgotPasswordPage } from '../auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../auth/ResetPasswordPage';
import { VerifyEmailPage } from '../auth/VerifyEmailPage';
import { AcceptInvitePage } from '../auth/AcceptInvitePage';
import { ContactsPage } from '../modules/contacts/ContactsPage';
import { ContactDetailPage } from '../modules/contacts/ContactDetailPage';
import { ContactFormPage } from '../modules/contacts/ContactFormPage';
import { ContactsImportPage } from '../modules/contacts/ContactsImportPage';
import { PipelinesPage } from '../modules/pipelines/PipelinesPage';
import { PipelineFormPage } from '../modules/pipelines/PipelineFormPage';
import { LeadsPage } from '../modules/leads/LeadsPage';
import { LeadDetailPage } from '../modules/leads/LeadDetailPage';
import { LeadFormPage } from '../modules/leads/LeadFormPage';
import { TasksPage } from '../modules/tasks/TasksPage';
import { TagsPage } from '../modules/tags/TagsPage';
import { FollowUpsPage } from '../modules/follow-ups/FollowUpsPage';
import { CustomFieldsPage } from '../modules/custom-fields/CustomFieldsPage';
import { ChannelsPage } from '../modules/channels/ChannelsPage';
import { InboxPage } from '../modules/conversations/InboxPage';
import { TenantsPage } from '../modules/admin/TenantsPage';
import { PlansPage } from '../modules/admin/PlansPage';
import { BillingPage } from '../modules/billing/BillingPage';
import { PricingPage } from '../modules/billing/PricingPage';
import { PlansOverviewPage } from '../modules/billing/PlansOverviewPage';
import { CompanyProfilePage } from '../modules/settings/CompanyProfilePage';
import { AutomationsPage } from '../modules/automations/AutomationsPage';
import { AutomationFormPage } from '../modules/automations/AutomationFormPage';
import { AutomationExecutionsPage } from '../modules/automations/AutomationExecutionsPage';
import { AutomationTemplatesPage } from '../modules/automation-templates/AutomationTemplatesPage';
import { AutomationTemplateUsePage } from '../modules/automation-templates/AutomationTemplateUsePage';
import { AutomationTemplatesAdminPage } from '../modules/automation-templates/AutomationTemplatesAdminPage';
import { AutomationTemplateAdminFormPage } from '../modules/automation-templates/AutomationTemplateAdminFormPage';
import { ProductsServicesPage } from '../modules/products-services/ProductsServicesPage';
import { ProductServiceFormPage } from '../modules/products-services/ProductServiceFormPage';
import { AppointmentsPage } from '../modules/appointments/AppointmentsPage';
import { OrdersPage } from '../modules/orders/OrdersPage';
import { QuotationsPage } from '../modules/quotations/QuotationsPage';
import { AutomationFlowsAdminPage } from '../modules/automation-flows/AutomationFlowsAdminPage';
import { AutomationFlowAdminFormPage } from '../modules/automation-flows/AutomationFlowAdminFormPage';
import { AutomationFlowsPage } from '../modules/automation-flows/AutomationFlowsPage';
import { AutomationFlowFormPage } from '../modules/automation-flows/AutomationFlowFormPage';
import { PrivacyPolicyPage } from '../modules/legal/PrivacyPolicyPage';
import { ProfilePage } from '../modules/profile/ProfilePage';
import { TeamPage } from '../modules/team/TeamPage';
import { CampaignsPage } from '../modules/campaigns/CampaignsPage';
import { CampaignFormPage } from '../modules/campaigns/CampaignFormPage';
import { RequestAccessPage } from '../modules/access-requests/RequestAccessPage';
import { AccessRequestsPage } from '../modules/admin/AccessRequestsPage';
import { PlanChangeRequestsPage } from '../modules/admin/PlanChangeRequestsPage';
import { AdminDashboardPage } from '../modules/admin/AdminDashboardPage';
import { ErrorLogsPage } from '../modules/admin/ErrorLogsPage';
import { PlatformAdminsPage } from '../modules/admin/PlatformAdminsPage';
import { PlatformMessagingPage } from '../modules/admin/PlatformMessagingPage';
import { AiUsagePage } from '../modules/admin/AiUsagePage';
import { BackgroundJobsPage } from '../modules/admin/BackgroundJobsPage';
import { RouteErrorPage } from '../errors/RouteErrorPage';

// Two `errorElement`s (RouteErrorPage reports the crash to `error_logs`): a
// pathless wrapper around every page inside AppLayout, so a crashing page
// keeps the sidebar/topbar and the user can navigate away; and one at the
// root for anything outside the shell (login, reset-password, ...).
export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    children: [
      // Truly public - reachable regardless of auth state. Reset/verify links must
      // stay reachable even for an already-authenticated browser (a different
      // account, or the same one after the link revokes its sessions), so these
      // are not wrapped in PublicOnlyRoute (which would redirect them to `/`).
      { path: '/privacy', element: <PrivacyPolicyPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/accept-invite', element: <AcceptInvitePage /> },
      {
        element: <PublicOnlyRoute />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/',
            element: <AppLayout />,
            children: [
              {
                errorElement: <RouteErrorPage />,
                children: [
                  { index: true, element: <DashboardPage /> },
                  { path: 'profile', element: <ProfilePage /> },
                  { path: 'team', element: <TeamPage /> },
                  { path: 'contacts', element: <ContactsPage /> },
                  { path: 'contacts/import', element: <ContactsImportPage /> },
                  { path: 'contacts/new', element: <ContactFormPage /> },
                  { path: 'contacts/:id', element: <ContactDetailPage /> },
                  { path: 'contacts/:id/edit', element: <ContactFormPage /> },
                  { path: 'pipelines', element: <PipelinesPage /> },
                  { path: 'pipelines/new', element: <PipelineFormPage /> },
                  { path: 'pipelines/:id/edit', element: <PipelineFormPage /> },
                  { path: 'leads', element: <LeadsPage /> },
                  { path: 'leads/new', element: <LeadFormPage /> },
                  { path: 'leads/:id', element: <LeadDetailPage /> },
                  { path: 'leads/:id/edit', element: <LeadFormPage /> },
                  { path: 'tasks', element: <TasksPage /> },
                  { path: 'follow-ups', element: <FollowUpsPage /> },
                  { path: 'tags', element: <TagsPage /> },
                  { path: 'custom-fields', element: <CustomFieldsPage /> },
                  { path: 'channels', element: <ChannelsPage /> },
                  { path: 'conversations', element: <InboxPage /> },
                  { path: 'admin/tenants', element: <TenantsPage /> },
                  { path: 'admin/plans', element: <PlansPage /> },
                  { path: 'billing', element: <BillingPage /> },
                  { path: 'plans', element: <PlansOverviewPage /> },
                  { path: 'settings/company', element: <CompanyProfilePage /> },
                  { path: 'automations', element: <AutomationsPage /> },
                  { path: 'automations/new', element: <AutomationFormPage /> },
                  { path: 'automations/:id/edit', element: <AutomationFormPage /> },
                  { path: 'automations/:id/executions', element: <AutomationExecutionsPage /> },
                  { path: 'automation-templates', element: <AutomationTemplatesPage /> },
                  { path: 'automation-templates/:id/use', element: <AutomationTemplateUsePage /> },
                  { path: 'admin/automation-templates', element: <AutomationTemplatesAdminPage /> },
                  {
                    path: 'admin/automation-templates/new',
                    element: <AutomationTemplateAdminFormPage />,
                  },
                  {
                    path: 'admin/automation-templates/:id/edit',
                    element: <AutomationTemplateAdminFormPage />,
                  },
                  { path: 'products-services', element: <ProductsServicesPage /> },
                  { path: 'products-services/new', element: <ProductServiceFormPage /> },
                  { path: 'products-services/:id/edit', element: <ProductServiceFormPage /> },
                  { path: 'appointments', element: <AppointmentsPage /> },
                  { path: 'orders', element: <OrdersPage /> },
                  { path: 'quotations', element: <QuotationsPage /> },
                  { path: 'admin/automation-flows', element: <AutomationFlowsAdminPage /> },
                  { path: 'admin/automation-flows/new', element: <AutomationFlowAdminFormPage /> },
                  {
                    path: 'admin/automation-flows/:id/edit',
                    element: <AutomationFlowAdminFormPage />,
                  },
                  { path: 'automation-flows', element: <AutomationFlowsPage /> },
                  { path: 'automation-flows/:id/edit', element: <AutomationFlowFormPage /> },
                  { path: 'campaigns', element: <CampaignsPage /> },
                  { path: 'campaigns/new', element: <CampaignFormPage /> },
                  { path: 'campaigns/:id', element: <CampaignFormPage /> },
                  { path: 'request-access', element: <RequestAccessPage /> },
                  { path: 'admin/access-requests', element: <AccessRequestsPage /> },
                  { path: 'admin/plan-change-requests', element: <PlanChangeRequestsPage /> },
                  { path: 'admin/dashboard', element: <AdminDashboardPage /> },
                  { path: 'admin/error-logs', element: <ErrorLogsPage /> },
                  { path: 'admin/platform-admins', element: <PlatformAdminsPage /> },
                  { path: 'admin/platform-messaging', element: <PlatformMessagingPage /> },
                  { path: 'admin/ai-usage', element: <AiUsagePage /> },
                  { path: 'admin/background-jobs', element: <BackgroundJobsPage /> },
                  { path: '*', element: <PageState kind="error" title="Page not found" /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
